/**
 * Le pointage de la garderie sur le téléphone de l'animateur : ce que la page
 * d'un lien sait sans la base.
 *
 * Un lien vaut pour une école et une année scolaire (migration
 * `20261031200000_le_pointage_de_la_garderie`). La page lit la liste d'un jour
 * par `garderie_lien_lire`, pointe par `garderie_lien_pointer`, et c'est la
 * base qui décide de tout ce qui compte : le lien est-il vivant, l'enfant
 * est-il de cette école, le mois est-il facturé. Ici : la forme des données,
 * les plages du jour, la plage par défaut, les groupes, la recherche, les
 * dates, les textes.
 */
import { jourDeLaSemaine } from './calcul';
import { attenteDuRefus, messageDAttente } from '../liens-codes';
import { plageDuScan, prochaineOuDerniere, type EntreeDeFile, type ReponseDuScan } from './scan';

export interface PlageDuLien {
  id: string;
  code: string;
  libelle: string;
  /** `HH:MM`. */
  heureDebut: string;
  heureFin: string;
  /** 1 = lundi … 5 = vendredi. */
  jours: number[];
  /**
   * Les réglages du scan (migration `20261103200000_le_scan_des_etiquettes`, SCAN-QR § 2.1) ;
   * une base qui ne les donne pas encore se lit avec les valeurs par défaut.
   * La plage que celle-ci implique (ATL2 → ATL1, MERAM → MERMIDI), ou `null`.
   */
  implique: string | null;
  /** Minutes avant le début où un scan compte déjà cette plage (15 ; 0 à la CSSA). */
  avanceMin: number;
  /** Minutes après la fin où un scan compte encore cette plage, « après la fin » (10). */
  retardMin: number;
  /** Minutes après le début où un scan compte encore la plage impliquée (5 pour ATL2 d'ISA, 0 sinon). */
  toleranceMin: number;
  /** `HH:MM` de l'appel (15:45 pour ATL1 d'ISA) : une « Sortie » avant ne pointe rien ; `null` sinon. */
  appelA: string | null;
  /** L'horaire propre à un jour (1 = lundi…), qui remplace `heureDebut`/`heureFin` ce jour-là (CSSA, lundi 14:55–18:00). */
  horairesDuJour: Record<number, { debut: string; fin: string }>;
}

export type Groupe = 'maternelle' | 'primaire';
export type FiltreDeGroupe = 'tous' | Groupe;

export interface EnfantDuLien {
  id: string;
  prenom: string;
  nom: string;
  groupe: Groupe | null;
  classe: string | null;
  /** Le numéro QuickSchool (`IDEleve` de l'étiquette), ou `null` s'il n'en a pas. */
  qsEleveId: string | null;
  /** Les plages où il est pointé ce jour-là (identifiants). */
  presences: string[];
  /**
   * Ses habitudes : par plage (identifiant), le nombre de jours où il y est venu dans les
   * 8 semaines avant le lundi de la semaine lue (migration `20261102180000`) ; `{}` si la base
   * ne les donne pas.
   */
  habitudes: Record<string, number>;
}

export interface LecturePointage {
  codeRequis: false;
  ecole: { id: string; nom: string; code: string };
  anneeScolaire: number;
  /** `AAAA-MM-JJ`. */
  date: string;
  aujourdHui: boolean;
  ferie: string | null;
  moisFacture: boolean;
  /** Le code de la plage que l'heure suggère, ou `null`. */
  plageProposee: string | null;
  plages: PlageDuLien[];
  enfants: EnfantDuLien[];
  /** L'heure du serveur au moment de la lecture (ISO), pour mesurer l'écart de l'horloge du téléphone ; `null` si la base ne la donne pas. */
  maintenant: string | null;
}

export type LectureDuLien = { codeRequis: true } | LecturePointage;

export interface ParentDeLaFiche {
  prenom: string | null;
  nom: string;
  telephone: string | null;
  reference: boolean;
  partPct: number;
}

export interface FicheGarderie {
  codeRequis: false;
  enfant: {
    id: string;
    prenom: string;
    nom: string;
    dateNaissance: string | null;
    classe: string | null;
    groupe: Groupe | null;
    rentreLeMidi: boolean;
    formule: string | null;
  };
  parents: ParentDeLaFiche[];
  depart: {
    partSeul: boolean | null;
    heure: string | null;
    personnes: { prenom: string | null; nom: string | null; telephone: string | null; lien: string | null }[];
  } | null;
  sante: {
    allergies: boolean | null;
    allergiesDetails: string | null;
    allergiesConsequences: string | null;
    regime: boolean | null;
    regimeDetails: string | null;
    medicaments: boolean | null;
    medicamentsDetails: string | null;
    medicamentsAutonomie: boolean | null;
    aSavoir: string | null;
    medecinNom: string | null;
    medecinTelephone: string | null;
  } | null;
}

export type LectureDeFiche = { codeRequis: true } | FicheGarderie;

export type ResultatDuPointage = { codeRequis: true } | { codeRequis: false; present: boolean };

/**
 * D'où vient la liste et où va le geste : le lien de l'animateur (sans compte,
 * jeton et code ; `pointage.supabase.ts`) ou Gestion (un compte de
 * l'administration ou de la coordination ; `presences-bureau.supabase.ts`).
 * L'écran est le même ; la base juge chaque geste dans les deux cas.
 */
export interface SourceDuPointage {
  /** La liste d'une date ; `{ codeRequis: true }` quand le lien demande son code. */
  lire(date: string): Promise<LectureDuLien>;
  /** Un ✓ (`present`) ou un ✗ ; l'état après le geste, tel que la base le dit. */
  pointer(enfantId: string, date: string, plageId: string, present: boolean): Promise<ResultatDuPointage>;
  /** La fiche de l'enfant (parents, départ, santé), quand la source la donne. */
  lireFiche?: (enfantId: string) => Promise<LectureDeFiche>;
  /**
   * Un lot de scans (1 à 25) de la file du téléphone, quand la source sait scanner
   * (`garderie_lien_scanner` ou `garderie_scanner`) : un résultat par clé, dans l'ordre.
   */
  scanner?: (entrees: readonly EntreeDeFile[]) => Promise<ReponseDuScan>;
  /** Ce sous quoi le téléphone garde le dernier groupe choisi. */
  cle: string;
}

/* ------------------------------------------------------------------------- */
/* Lecture des réponses                                                       */
/* ------------------------------------------------------------------------- */

type J = Record<string, unknown>;
const o = (v: unknown): J => (v && typeof v === 'object' && !Array.isArray(v) ? (v as J) : {});
const s = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const b = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const n = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
const tableau = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const groupe = (v: unknown): Groupe | null => (v === 'maternelle' || v === 'primaire' ? v : null);
/** Un nombre de minutes, ou la valeur par défaut quand la base ne le donne pas (encore). */
const minutes = (v: unknown, defaut: number): number => {
  if (v === null || v === undefined || v === '') return defaut;
  const x = Number(v);
  return Number.isFinite(x) && x >= 0 ? Math.trunc(x) : defaut;
};
/** `HH:MM` (les secondes éventuelles retirées), ou `null`. */
const heureOuNull = (v: unknown): string | null => (typeof v === 'string' && /^\d{2}:\d{2}/.test(v) ? v.slice(0, 5) : null);
/** Le numéro QuickSchool : du texte, ou un nombre reçu tel quel. */
const numero = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : typeof v === 'number' && Number.isFinite(v) ? String(v) : null);

/** `{ "1": { "debut": "14:55", "fin": "18:00" } }` → par jour (1 à 7) ; le reste est ignoré. */
export function lireHorairesDuJour(v: unknown): Record<number, { debut: string; fin: string }> {
  const out: Record<number, { debut: string; fin: string }> = {};
  for (const [k, x] of Object.entries(o(v))) {
    const jour = Number(k);
    const h = o(x);
    const debut = heureOuNull(h.debut);
    const fin = heureOuNull(h.fin);
    if (Number.isInteger(jour) && jour >= 1 && jour <= 7 && debut && fin && debut < fin) out[jour] = { debut, fin };
  }
  return out;
}

/**
 * Les réglages du scan d'une plage, telle que `garderie_lien_lire` ou `garderie_semaine` la
 * rendent ; valeurs par défaut (15, 10, 0, `null`, `{}`) pour une base d'avant la migration
 * `20261103200000`.
 */
export function lireReglagesDePlage(x: Record<string, unknown>): Pick<PlageDuLien, 'implique' | 'avanceMin' | 'retardMin' | 'toleranceMin' | 'appelA' | 'horairesDuJour'> {
  return {
    implique: s(x.implique) ?? s(x.implique_plage_id),
    avanceMin: minutes(x.avance_min, 15),
    retardMin: minutes(x.retard_min, 10),
    toleranceMin: minutes(x.tolerance_min, 0),
    appelA: heureOuNull(x.appel_a),
    horairesDuJour: lireHorairesDuJour(x.horaires_du_jour),
  };
}

/** `{ "<plage_id>": jours }` → des nombres entiers positifs ; le reste est ignoré. */
export const lireHabitudes = (v: unknown): Record<string, number> =>
  Object.fromEntries(
    Object.entries(o(v))
      .map(([k, x]) => [k, n(x)] as const)
      .filter(([, x]) => Number.isFinite(x) && x > 0),
  );

export function lireLecture(data: unknown): LectureDuLien {
  const d = o(data);
  if (d.code_requis === true) return { codeRequis: true };
  const ecole = o(d.ecole);
  return {
    codeRequis: false,
    ecole: { id: s(ecole.id) ?? '', nom: s(ecole.nom) ?? '', code: s(ecole.code) ?? '' },
    anneeScolaire: n(d.annee_scolaire),
    date: s(d.date) ?? '',
    aujourdHui: d.aujourd_hui === true,
    ferie: s(d.ferie),
    moisFacture: d.mois_facture === true,
    plageProposee: s(d.plage_proposee),
    plages: tableau(d.plages).map((p) => {
      const x = o(p);
      return {
        id: s(x.id) ?? '',
        code: s(x.code) ?? '',
        libelle: s(x.libelle) ?? '',
        heureDebut: s(x.heure_debut) ?? '',
        heureFin: s(x.heure_fin) ?? '',
        jours: tableau(x.jours).map(n),
        ...lireReglagesDePlage(x),
      };
    }),
    enfants: tableau(d.enfants).map((e) => {
      const x = o(e);
      return {
        id: s(x.id) ?? '',
        prenom: s(x.prenom) ?? '',
        nom: s(x.nom) ?? '',
        groupe: groupe(x.groupe),
        classe: s(x.classe),
        qsEleveId: numero(x.qs_eleve_id),
        presences: tableau(x.presences).map((p) => String(p)),
        habitudes: lireHabitudes(x.habitudes),
      };
    }),
    maintenant: s(d.maintenant),
  };
}

export function lireFiche(data: unknown): LectureDeFiche {
  const d = o(data);
  if (d.code_requis === true) return { codeRequis: true };
  const e = o(d.enfant);
  const dep = d.depart && typeof d.depart === 'object' ? o(d.depart) : null;
  const sa = d.sante && typeof d.sante === 'object' ? o(d.sante) : null;
  return {
    codeRequis: false,
    enfant: {
      id: s(e.id) ?? '',
      prenom: s(e.prenom) ?? '',
      nom: s(e.nom) ?? '',
      dateNaissance: s(e.date_naissance),
      classe: s(e.classe),
      groupe: groupe(e.groupe),
      rentreLeMidi: e.rentre_le_midi === true,
      formule: s(e.formule),
    },
    parents: tableau(d.parents).map((p) => {
      const x = o(p);
      return { prenom: s(x.prenom), nom: s(x.nom) ?? '', telephone: s(x.telephone), reference: x.reference === true, partPct: n(x.part_pct) };
    }),
    depart: dep
      ? {
          partSeul: b(dep.part_seul),
          heure: s(dep.heure),
          personnes: tableau(dep.personnes).map((p) => {
            const x = o(p);
            return { prenom: s(x.prenom), nom: s(x.nom), telephone: s(x.telephone), lien: s(x.lien) };
          }),
        }
      : null,
    sante: sa
      ? {
          allergies: b(sa.allergies),
          allergiesDetails: s(sa.allergies_details),
          allergiesConsequences: s(sa.allergies_consequences),
          regime: b(sa.regime),
          regimeDetails: s(sa.regime_details),
          medicaments: b(sa.medicaments),
          medicamentsDetails: s(sa.medicaments_details),
          medicamentsAutonomie: b(sa.medicaments_autonomie),
          aSavoir: s(sa.a_savoir),
          medecinNom: s(sa.medecin_nom),
          medecinTelephone: s(sa.medecin_telephone),
        }
      : null,
  };
}

/* ------------------------------------------------------------------------- */
/* Les plages, les groupes, la recherche                                      */
/* ------------------------------------------------------------------------- */

/** Les plages qui tournent ce jour-là (le mercredi, l'après-midi ; le week-end, rien). */
export function plagesDuJour(plages: readonly PlageDuLien[], date: string): PlageDuLien[] {
  const jour = jourDeLaSemaine(date);
  return plages.filter((p) => p.jours.includes(jour));
}

/**
 * La plage qu'ouvre l'heure (demande du 02/11/2026 : « matin si on est le matin,
 * ATL si l'après-midi ») : celle qui tourne maintenant (la dernière commencée, ATL2
 * à 16 h et non ATL1) ; sinon, le matin, la plage du matin ; l'après-midi, la
 * prochaine à commencer, ou la dernière de la journée quand elle est finie. Une
 * école sans plage du matin (la CSSA) ouvre sa plage de l'après-midi. Les heures
 * sont des `HH:MM`.
 */
export function plageDeLHeure<T extends { heureDebut: string; heureFin: string }>(duJour: readonly T[], heure: string): T | null {
  if (duJour.length === 0) return null;
  const h = heure.slice(0, 5);
  const tries = [...duJour].sort((a, b) => a.heureDebut.localeCompare(b.heureDebut));
  const enCours = tries.filter((p) => p.heureDebut.slice(0, 5) <= h && h < p.heureFin.slice(0, 5));
  if (enCours.length > 0) return enCours[enCours.length - 1];
  const matin = h < '12:00';
  const duCote = tries.filter((p) => (p.heureDebut.slice(0, 5) < '12:00') === matin);
  if (duCote.length === 0) return matin ? tries[0] : tries[tries.length - 1];
  if (matin) {
    const commencees = duCote.filter((p) => p.heureDebut.slice(0, 5) <= h);
    return commencees.length > 0 ? commencees[commencees.length - 1] : duCote[0];
  }
  return duCote.find((p) => p.heureDebut.slice(0, 5) > h) ?? duCote[duCote.length - 1];
}

/**
 * La plage à ouvrir : celle que l'animateur a choisie si elle tourne ce jour-là ;
 * aujourd'hui, celle qu'un scan poserait à cette heure (`plageDuScan`, tolérance
 * comprise, sans tenir compte du mode : SCAN-QR § 2.1), pour qu'un ✓ et un scan à la
 * même heure comptent la même plage — et quand la règle ne donne aucune plage (« à
 * vérifier », « pas de garderie »), la prochaine plage du jour, sinon la dernière ;
 * un autre jour, celle que la base propose, sinon la première du jour. L'écran
 * l'appelle une fois, à l'ouverture d'une date, puis la garde : la liste ne change
 * pas de plage toute seule quand l'horloge passe 16 h. PROPOSITION TECHNIQUE.
 */
export function plageParDefaut(lecture: LecturePointage, duJour: readonly PlageDuLien[], choisie: string | null, heure?: string): string | null {
  if (choisie && duJour.some((p) => p.id === choisie)) return choisie;
  if (lecture.aujourdHui && heure && duJour.length > 0) {
    const r = plageDuScan(duJour, lecture.date, heure, null, lecture.ferie !== null);
    if ('plage' in r && duJour.some((p) => p.id === r.plage.id)) return r.plage.id;
    /* Entre deux plages (10h, 13h) : la règle d'avant, « le matin, la plage du matin ; l'après-midi,
       la prochaine » (CADRAGE § 4.1) — pas l'ATL1 dès 8h20 (relecture du 03/11/2026). */
    return (plageDeLHeure(duJour, heure) ?? prochaineOuDerniere(duJour, lecture.date, heure))?.id ?? null;
  }
  const proposee = duJour.find((p) => p.code === lecture.plageProposee);
  if (proposee) return proposee.id;
  return duJour[0]?.id ?? null;
}

/**
 * Une plage ne se pointe qu'à partir d'une demi-heure avant son début, le jour même (demande
 * d'Éclosion du 04/11/2026, après des arrivées du matin cochées sur le mercredi midi à 7h54). La
 * base refuse aussi (`plage_pas_commencee:HH:MM`, migration `20261104120000`). Un jour passé se
 * rattrape librement ; un ✓ posé par erreur se retire toujours.
 */
export const MARGE_AVANT_PLAGE_MIN = 30;
/**
 * L'écran ne bloque une case qu'avec cette tolérance de plus. Il juge à l'heure corrigée sur
 * celle du serveur (`ecartAuServeur` de `scan.ts`, tenu par `useFileDeScans`) ; l'écart peut
 * rester imprécis de quelques instants. Entre les deux, la base tranche.
 */
export const TOLERANCE_HORLOGE_MIN = 5;

const enMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.slice(0, 5).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
const enHhmm = (minutes: number): string => {
  const x = Math.max(0, minutes);
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};

/** « 7h15 », « 12h » : l'heure comme on la dit. */
export function heureEnClair(hhmm: string): string {
  const [h, m] = hhmm.slice(0, 5).split(':');
  return `${Number(h)}h${m === '00' ? '' : m}`;
}

type HorairesDuJour = Record<number, { debut: string; fin: string }>;

/** L'heure de début d'une plage ce jour-là : celle de son horaire du jour (la CSSA le lundi), sinon la sienne. */
export function debutDuJour(plage: { heureDebut: string; horairesDuJour?: HorairesDuJour }, date: string): string {
  return plage.horairesDuJour?.[jourDeLaSemaine(date)]?.debut ?? plage.heureDebut.slice(0, 5);
}

/** L'heure de fin d'une plage ce jour-là. */
export function finDuJour(plage: { heureFin: string; horairesDuJour?: HorairesDuJour }, date: string): string {
  return plage.horairesDuJour?.[jourDeLaSemaine(date)]?.fin ?? plage.heureFin.slice(0, 5);
}

/** L'heure à partir de laquelle se pointe une plage qui commence à `debut` (`HH:MM`). */
export function ouvertureDuPointage(debut: string): string {
  return enHhmm(enMinutes(debut) - MARGE_AVANT_PLAGE_MIN);
}

/**
 * Vrai quand la liste lue est celle d'aujourd'hui et que la plage ne se pointe pas encore à
 * `heure` ; `toleranceMin` recule le seuil (l'écran bloque avec la tolérance de l'horloge).
 */
export function plagePasCommencee(
  plage: { heureDebut: string; horairesDuJour?: HorairesDuJour },
  date: string,
  aujourdHui: boolean,
  heure: string,
  toleranceMin = 0,
): boolean {
  return aujourdHui && enMinutes(heure) < enMinutes(ouvertureDuPointage(debutDuJour(plage, date))) - toleranceMin;
}

const sansAccents = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** La valeur du filtre de classe qui garde les enfants sans classe connue. */
export const SANS_CLASSE = '∅';

/**
 * Le filtre : un groupe, une classe (`''` : toutes ; `SANS_CLASSE` : les enfants sans classe),
 * et une recherche par début de prénom ou de nom.
 */
export function filtrerEnfants(enfants: readonly EnfantDuLien[], groupe: FiltreDeGroupe, recherche: string, classe = ''): EnfantDuLien[] {
  const q = sansAccents(recherche.trim());
  return enfants.filter((e) => {
    if (groupe !== 'tous' && e.groupe !== groupe) return false;
    if (classe === SANS_CLASSE ? e.classe !== null : classe !== '' && e.classe !== classe) return false;
    if (!q) return true;
    return sansAccents(e.prenom).startsWith(q) || sansAccents(e.nom).startsWith(q) || sansAccents(`${e.prenom} ${e.nom}`).startsWith(q);
  });
}

const alphabet = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/**
 * L'ordre de la liste (demande du 02/11/2026) : d'abord les enfants qui viennent le plus
 * souvent sur la plage ouverte — le matin, les habitués du matin —, puis l'ordre alphabétique
 * (nom, puis prénom). Les habitudes sont comptées avant le lundi de la semaine lue (le même ordre
 * sur le téléphone et dans Gestion) : cocher ne déplace personne.
 * Sans plage, l'ordre alphabétique seul.
 */
export function trierParHabitude<T extends { id: string; nom: string; prenom: string; habitudes: Record<string, number> }>(
  enfants: readonly T[],
  plageId: string | null,
): T[] {
  const jours = (e: T) => (plageId ? (e.habitudes[plageId] ?? 0) : 0);
  return [...enfants].sort(
    (a, b) => jours(b) - jours(a) || alphabet.compare(a.nom, b.nom) || alphabet.compare(a.prenom, b.prenom) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/**
 * Une relecture (toutes les 15 s) rafraîchit les ✓, jamais l'ordre : pour la même école et la
 * même date, chaque enfant garde les habitudes de la première lecture. Sans cela, un jour passé
 * réencodé ailleurs (un collègue, Gestion) déplacerait des lignes sous le doigt de l'animateur.
 * Un enfant nouveau dans la liste garde les siennes ; une autre date ou une autre école repart
 * de la lecture reçue.
 */
export function garderLesHabitudes(avant: LecturePointage | null, apres: LecturePointage): LecturePointage {
  if (!avant || avant.date !== apres.date || avant.ecole.id !== apres.ecole.id) return apres;
  const connues = new Map(avant.enfants.map((e) => [e.id, e.habitudes]));
  return {
    ...apres,
    enfants: apres.enfants.map((e) => {
      const h = connues.get(e.id);
      return h && h !== e.habitudes ? { ...e, habitudes: h } : e;
    }),
  };
}

const ordreDesClasses = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/**
 * Les classes à proposer dans le filtre, pour le groupe choisi : chacune avec son nombre
 * d'enfants, dans l'ordre naturel (M1 avant M2, P2 avant P10) ; et le nombre d'enfants sans
 * classe connue.
 */
export function classesDeLaListe(enfants: readonly EnfantDuLien[], groupe: FiltreDeGroupe): { classes: { classe: string; nombre: number }[]; sansClasse: number } {
  const parClasse = new Map<string, number>();
  let sansClasse = 0;
  for (const e of enfants) {
    if (groupe !== 'tous' && e.groupe !== groupe) continue;
    if (e.classe === null) sansClasse += 1;
    else parClasse.set(e.classe, (parClasse.get(e.classe) ?? 0) + 1);
  }
  return {
    classes: [...parClasse.entries()].map(([classe, nombre]) => ({ classe, nombre })).sort((a, b) => ordreDesClasses.compare(a.classe, b.classe)),
    sansClasse,
  };
}

/** Le nombre d'enfants pointés sur une plage, dans la liste donnée. */
export function compterPresents(enfants: readonly EnfantDuLien[], plageId: string | null): number {
  if (!plageId) return 0;
  return enfants.filter((e) => e.presences.includes(plageId)).length;
}

/** Le nombre d'enfants par groupe. */
export function compterParGroupe(enfants: readonly EnfantDuLien[]): Record<FiltreDeGroupe, number> {
  return {
    tous: enfants.length,
    maternelle: enfants.filter((e) => e.groupe === 'maternelle').length,
    primaire: enfants.filter((e) => e.groupe === 'primaire').length,
  };
}

/** La liste après un geste : la présence ajoutée ou retirée, sans toucher au reste. */
export function appliquerPointage(enfants: readonly EnfantDuLien[], enfantId: string, plageId: string, present: boolean): EnfantDuLien[] {
  return enfants.map((e) => {
    if (e.id !== enfantId) return e;
    const sans = e.presences.filter((p) => p !== plageId);
    return { ...e, presences: present ? [...sans, plageId] : sans };
  });
}

/* ------------------------------------------------------------------------- */
/* Les dates                                                                  */
/* ------------------------------------------------------------------------- */

/** `AAAA-MM-JJ` à Bruxelles, maintenant. */
export function aujourdHuiBruxelles(maintenant: Date = new Date()): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).format(maintenant);
}

/** `HH:MM` à Bruxelles, maintenant (24 h). */
export function heureBruxelles(maintenant: Date = new Date()): string {
  const parties = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(maintenant);
  const valeur = (type: string) => parties.find((p) => p.type === type)?.value ?? '00';
  return `${valeur('hour').padStart(2, '0')}:${valeur('minute').padStart(2, '0')}`;
}

const decaler = (date: string, jours: number): string => {
  const [a, m, j] = date.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j + jours));
  return d.toISOString().slice(0, 10);
};
export const veille = (date: string): string => decaler(date, -1);
export const lendemain = (date: string): string => decaler(date, 1);

const JOURS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MOIS = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** « lundi 5 octobre 2026 ». */
export function dateEnClair(date: string): string {
  const [a, m, j] = date.split('-').map(Number);
  if (!a || !m || !j) return date;
  return `${JOURS[jourDeLaSemaine(date)]} ${j === 1 ? '1er' : j} ${MOIS[m]} ${a}`;
}

/** L'âge en années révolues, ou `null`. */
export function ageEnAnnees(dateNaissance: string | null, aujourdHui: string): number | null {
  if (!dateNaissance) return null;
  const [an, mn, jn] = dateNaissance.split('-').map(Number);
  const [a, m, j] = aujourdHui.split('-').map(Number);
  if (!an || !a) return null;
  let age = a - an;
  if (m < mn || (m === mn && j < jn)) age -= 1;
  return age;
}

/* ------------------------------------------------------------------------- */
/* Les textes                                                                 */
/* ------------------------------------------------------------------------- */

export function initiales(prenom: string, nom: string): string {
  return `${prenom.trim().charAt(0)}${nom.trim().charAt(0)}`.toUpperCase() || '?';
}

/** Ce que la base refuse, en français. */
export function messageDeRefus(message: string): string {
  const attente = attenteDuRefus(message);
  if (attente !== null) return messageDAttente(attente);
  const pasCommencee = /plage_pas_commencee:(\d{2}:\d{2})/.exec(message);
  if (pasCommencee) {
    const debut = pasCommencee[1];
    return `Cette plage commence à ${heureEnClair(debut)} : elle se pointe à partir de ${heureEnClair(ouvertureDuPointage(debut))}.`;
  }
  if (message.startsWith('[garderie-mois-facture]')) return 'Ce mois est déjà facturé : la présence ne peut plus changer. Signalez-la à la coordination.';
  switch (message) {
    case 'lien_inconnu':
      return 'Ce lien n’existe pas. Demandez le bon lien à la coordination.';
    case 'lien_revoque':
      return 'Ce lien a été remplacé. Demandez le nouveau lien à la coordination.';
    case 'lien_expire':
      return 'Ce lien n’est plus valable : l’année scolaire est finie.';
    case 'lien_bloque':
      return 'Ce lien est bloqué après trop de codes faux. La coordination doit en créer un autre.';
    case 'date_future':
      return 'On ne pointe pas une date à venir.';
    case 'date_hors_annee':
      return 'Cette date est hors de l’année scolaire.';
    case 'enfant_hors_lien':
      return 'Cet enfant n’est pas de cette école.';
    case 'plage_hors_lien':
      return 'Cette plage n’existe pas pour cette école.';
    case 'rien_a_changer':
      return 'Rien à enregistrer.';
    case 'scans_invalides':
      return 'Le téléphone a envoyé des scans illisibles : rechargez la page.';
    case 'trop_de_scans':
      return 'Trop de scans envoyés d’un coup : le téléphone les renvoie par petits lots.';
    case 'code_requis':
      return 'Retapez le code du lien : les scans attendent sur le téléphone.';
    default:
      if (message.startsWith('[reserve-gestion]') || message.startsWith('réservé à l')) return 'Réservé à l’administration et à la coordination.';
      return message || 'Quelque chose n’a pas marché. Réessayez.';
  }
}

/** Le texte brut d'une erreur : un `Error`, un objet PostgREST `{ message, code, … }`, ou `''`. */
export function texteDeLErreur(e: unknown): string {
  if (e instanceof Error) return e.message.trim();
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const m = (e as { message: unknown }).message;
    if (typeof m === 'string') return m.trim();
  }
  return '';
}

/**
 * Une erreur de réseau (pas de réponse de la base : wifi coupé, délai), qu'on peut réessayer,
 * par opposition à un refus de la base. HYPOTHÈSE À VÉRIFIER : les textes de `fetch` varient
 * selon le navigateur (« Failed to fetch », « Load failed », « NetworkError… »).
 */
export function estErreurDeReseau(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (e instanceof TypeError) return true;
  if (e instanceof Error && e.name === 'AbortError') return true;
  /* Un délai dépassé (`AbortSignal.timeout` de src/lib/supabase.ts) : postgrest-js le rend
     « TimeoutError: signal timed out » (Chrome) ou « … The operation timed out. » (Safari, Firefox). */
  return /failed to fetch|load failed|networkerror|network request failed|fetch failed|timeouterror|aborterror|timed out/i.test(texteDeLErreur(e));
}

/** Les refus qui ne passeront plus jamais par ce lien : il a été remplacé, il a expiré, il est bloqué ou n'existe pas. */
export const REFUS_DEFINITIFS_DU_LIEN = ['lien_inconnu', 'lien_revoque', 'lien_expire', 'lien_bloque'] as const;

/**
 * Vrai quand l'erreur dit que le lien est mort (`REFUS_DEFINITIFS_DU_LIEN`) : la file arrête
 * alors de renvoyer par lui (les scans restent sur le téléphone et partiront par le nouveau
 * lien de l'école). Une pause après des codes faux (`lien_ralenti:…`) n'en est pas un.
 */
export function estRefusDefinitifDuLien(e: unknown): boolean {
  return (REFUS_DEFINITIFS_DU_LIEN as readonly string[]).includes(texteDeLErreur(e));
}

/**
 * Le message à montrer pour une erreur, quelle que soit sa forme : les objets PostgREST
 * (qui ne sont pas des `Error` et s'affichaient « [object Object] », SCAN-QR § 7, 3) comme
 * les `Error`, traduits par `messageDeRefus` ; une erreur de réseau a son propre texte.
 */
export function messageDErreur(e: unknown): string {
  if (estErreurDeReseau(e)) return 'Pas de réseau : réessayez dans un instant.';
  return messageDeRefus(texteDeLErreur(e));
}

export function adresseDuLien(origine: string, jeton: string): string {
  return `${origine.replace(/\/$/, '')}/garderie/${jeton}`;
}

/** Le message à coller dans le groupe WhatsApp de l'école. */
export function messagePourLesAnims(args: { ecole: string; adresse: string; code: string | null }): string {
  const lignes = [
    `Pointage garderie ${args.ecole} :`,
    args.adresse,
    '',
    'Ouvrez ce lien sur votre téléphone, choisissez la plage (matin, ATL, mercredi) et cochez les enfants présents. Chaque ✓ s’enregistre tout seul ; décocher retire la présence.',
  ];
  if (args.code) lignes.push('', `Code : ${args.code} (à garder pour vous).`);
  return lignes.join('\n');
}
